import { useState, useEffect, useCallback } from "react";

export interface ColumnVisibilityState {
    [key: string]: boolean;
}

const DEFAULT_ORDER_COLUMNS: ColumnVisibilityState = {
    order_number: true,
    customer_name: true,
    status: true,
    total_amount: true,
    total_cost_price: false,
    profit: false,
    shipping_amount: true,
    delivery_service: true,
    order_channel: false,
    order_date: true,
    payment_status: true,
    actions: true,
};

const DEFAULT_PRODUCT_COLUMNS: ColumnVisibilityState = {
    thumbnail: true,
    name: true,
    available_stock: true,
    category: true,
    brand: true,
    selling_price: true,
    cost_price: true,
    is_active: true,
    create_time: true,
    clone_action: true,
};

const STORAGE_KEYS = {
    orders: "admin_order_columns_visibility",
    products: "admin_product_columns_visibility",
};

export function useColumnVisibility(
    tableType: "orders" | "products" = "orders",
) {
    const getDefaultColumns = useCallback(() => {
        return tableType === "orders"
            ? DEFAULT_ORDER_COLUMNS
            : DEFAULT_PRODUCT_COLUMNS;
    }, [tableType]);

    const getStorageKey = useCallback(() => {
        return STORAGE_KEYS[tableType];
    }, [tableType]);

    const [columnVisibility, setColumnVisibility] =
        useState<ColumnVisibilityState>(getDefaultColumns());

    // Load from localStorage on mount
    useEffect(() => {
        try {
            const saved = localStorage.getItem(getStorageKey());
            if (saved) {
                const parsed = JSON.parse(saved);
                setColumnVisibility((prev) => ({
                    ...getDefaultColumns(),
                    ...parsed,
                }));
            }
        } catch (error) {
            console.warn(
                "Failed to load column visibility from localStorage:",
                error,
            );
        }
    }, [tableType, getDefaultColumns, getStorageKey]);

    // Save to localStorage whenever visibility changes
    const updateColumnVisibility = (columnId: string, visible: boolean) => {
        setColumnVisibility((prev) => {
            const updated = { ...prev, [columnId]: visible };

            try {
                localStorage.setItem(getStorageKey(), JSON.stringify(updated));
            } catch (error) {
                console.warn(
                    "Failed to save column visibility to localStorage:",
                    error,
                );
            }

            return updated;
        });
    };

    const resetToDefaults = () => {
        const defaults = getDefaultColumns();
        setColumnVisibility(defaults);
        try {
            localStorage.setItem(getStorageKey(), JSON.stringify(defaults));
        } catch (error) {
            console.warn(
                "Failed to reset column visibility to defaults:",
                error,
            );
        }
    };

    const toggleColumn = (columnId: string) => {
        updateColumnVisibility(columnId, !columnVisibility[columnId]);
    };

    return {
        columnVisibility,
        updateColumnVisibility,
        toggleColumn,
        resetToDefaults,
    };
}
